import type { EntradaDeClubeCriado,
  ClubCrest,
  ClubKit,
} from "@legacy-manager/engine";
/**
 * Projeções e comandos que cruzam a fronteira do Web Worker.
 *
 * O mundo inteiro tem megabytes e nunca é enviado para a interface. O worker
 * monta visões pequenas e recebe comandos, que é o mesmo contrato que o modo
 * online vai usar quando o servidor filtrar o estado por membro da liga.
 *
 * Nada aqui muda o mundo na hora. As decisões do treinador entram numa fila e
 * só valem quando a rodada é processada, exatamente como acontecerá no
 * servidor: é o mesmo caminho, mudando apenas quem executa.
 *
 * E **nenhuma frase atravessa esta fronteira**. O worker tem a própria cópia do
 * i18n e nunca soube qual idioma a pessoa escolheu, então texto escrito lá sai
 * em português para quem joga em inglês. O que atravessa é chave e valores, e
 * quem escreve a frase é a tela.
 */

import { SaveError, type Datapack } from "@legacy-manager/engine";

import type { SaveMeta } from "./save-store";

export type { SaveMeta };

/**
 * Uma frase que nasce do lado do worker, como chave e valores.
 *
 * É a moeda desta fronteira: recusa, aviso, resumo de decisão na fila. A chave
 * não passa pelo tipo `Chave` de propósito, porque ela também pode vir do motor
 * e porque chave desconhecida precisa ter um caminho de volta em vez de virar
 * erro de compilação.
 */
export interface FraseDaSessao {
  key: string;
  params: Record<string, string | number>;
}

export interface ClubOption {
  id: string;
  name: string;
  short: string;
  colors: [string, string];
  /**
   * O código do país, como `BR`.
   *
   * Vem junto do nome porque quem cria um clube precisa dizer ao motor onde ele
   * nasce, e o motor indexa país por código. O nome não serve: ele é o que a
   * tela mostra e muda de idioma.
   */
  countryId: string;
  countryName: string;
  leagueName: string;
  tier: number;
  reputation: number;
  /** O porte do clube, como chave de rótulo do motor. */
  reputationKey: string;
  squadStrength: number;
  cash: number;
}

/** Setor a que a posição pertence, usado para agrupar o elenco. */
export type Sector = "GOL" | "DEF" | "MEI" | "ATA";

export interface PlayerRow {
  id: string;
  name: string;
  pos: string;
  sector: Sector;
  age: number;
  nationality: string;
  strength: number;
  highlights: string[];
  morale: number;
  condition: number;
  value: number;
  salary: number;
  shirt: number | null;
  injuryRounds: number;
  suspensionRounds: number;
  goals: number;
  assists: number;
  apps: number;
  /** Amarelos na temporada. A cada três o motor suspende, então dois é pendurado. */
  yellow: number;
  rating: number;
  starter: boolean;
  benched: boolean;
  /** Posição em que está escalado, que pode não ser a natural. */
  playingPos: string | null;
  contractEndsSeason: number;
  transferListed: boolean;
}

/** Ficha completa, com os quatorze atributos por trás da força. */
/** Laudo de olheiro como a tela mostra. O potencial em si nunca vem junto. */
export interface ScoutView {
  low: number;
  high: number;
  confidence: string;
  /** O veredito do olheiro, como chave de rótulo do motor. */
  verdictKey: string;
  /** As observações, como chave. `key: null` é laudo de save antigo. */
  notes: Array<{ key: string } | { key: null; text: string }>;
  /** O laudo envelheceu e vale a pena pedir outro. */
  stale: boolean;
}

export interface PlayerDetail extends PlayerRow {
  clubName: string;
  clubId: string | null;
  /** Sigla, cores e escudo desenhado do clube atual, para o escudo da ficha. */
  clubShort: string;
  clubColors: [string, string] | null;
  clubCrest: ClubCrest | null;
  /** Sigla do motor (`D`, `E`, `A`), que a tela escreve com `ficha.pes`. */
  foot: string;
  /** Sigla do motor (`lider`), que a tela escreve com `ficha.personalidades`. */
  personality: string;
  /** A sigla do motor (`arm`), que a tela escreve com `rotuloDeCaracteristica`. */
  attrs: Array<{ key: string; value: number; weighted: boolean }>;
  careerApps: number;
  careerGoals: number;
  /** Amarelos e vermelhos na temporada. O motor já contava; ninguém mostrava. */
  yellow: number;
  red: number;
  /** Menor salário que ele aceita para vir ao seu clube. Nulo quando não vem. */
  askingSalary: number | null;
  /** Preço em que o clube dono começa a ouvir proposta. */
  askingPrice: number;
  /**
   * Preço que fecha a compra na hora, sem proposta e sem leilão. Zero para
   * jogador livre. Nulo quando não dá para comprar assim: jogador emprestado,
   * ou de clube que outro treinador dirige.
   */
  buyPrice: number | null;
  isMine: boolean;
  /** Laudo de olheiro que o seu clube já tem sobre ele. */
  scout: ScoutView | null;
  /** Quanto custa mandar observar. */
  scoutCost: number;
  /** Fatia do salário que você teria de assumir para pegá-lo emprestado. */
  loanShare: number;
  /** Motivo de o empréstimo não ser possível, ou nulo quando é. */
  /** Por que o empréstimo não sai, como recusa do motor. */
  loanBlocked: Recusa | null;
  /** Preenchido quando ele já está emprestado a alguém. */
  onLoan: { ownerName: string; borrowerName: string; salaryShare: number } | null;
  /**
   * Leilão em andamento por este jogador.
   *
   * Quando dois ou mais clubes propõem na mesma rodada, a maior oferta é ouvida
   * primeiro. Saber que há disputa, e por quanto, é o que transforma a proposta
   * numa decisão em vez de um chute.
   */
  auction: { bidders: number; topBid: number; mineIsTop: boolean } | null;
}

/** O que dá para fazer com um garoto que chegou à idade de decidir. */
export type YouthAction = "promover" | "manter" | "dispensar";

/**
 * Um garoto da base, do jeito que a tela mostra.
 *
 * Espelha a projeção do motor e não carrega o potencial cru, pela mesma razão
 * que a ficha do jogador não carrega: o que se vê é a faixa. O que este tipo
 * acrescenta é a decisão que já está na fila, para a tela não oferecer duas
 * vezes o mesmo botão.
 */
export interface YouthRow {
  id: string;
  name: string;
  pos: string;
  age: number;
  nationality: string;
  foot: string;
  personality: string;
  strength: number;
  /** Força com que ele entrou na base. */
  entryStrength: number;
  /** Quanto ele cresceu desde a entrada. */
  growth: number;
  potentialLow: number;
  potentialHigh: number;
  confidence: string;
  /** Veredito da comissão, como chave de texto. */
  verdict: string;
  highlights: string[];
  seasonsInAcademy: number;
  /** Passou o ano parado, e o teto encolheu junto. */
  stagnant: boolean;
  /** Está na idade de decidir. */
  ready: boolean;
  /** Última temporada em que ele ainda pode ficar na base. */
  lastSeasonAllowed: number;
  /** Decisão já enfileirada para ele, ou nulo. */
  pendingAction: YouthAction | null;
}

/** Convite de outro clube ao treinador, na tela de fim de temporada. */
export interface JobOfferView {
  clubId: string;
  clubName: string;
  clubShort: string;
  colors: [string, string];
  countryName: string;
  leagueName: string;
  /** O que a diretoria de lá vai cobrar, como chave de rótulo do motor. */
  objectiveKey: string;
  budget: number;
  /** O clube por dentro, para decidir antes de assumir. Nulo se o clube sumiu do mundo. */
  preview: ClubPreview | null;
}

/** Um clube visto de fora: números, estádio e o elenco inteiro. */
export interface ClubPreview {
  reputation: number;
  reputationKey: string;
  stadiumCapacity: number;
  debt: number;
  wagesPerRound: number;
  /** Posição na liga ao fim da temporada; zero quando não há tabela. */
  position: number;
  /** Média dos dezesseis mais fortes, a mesma conta que o motor usa. */
  squadStrength: number;
  averageAge: number;
  squad: Array<{ id: string; name: string; pos: string; sector: Sector; age: number; strength: number }>;
}

/** Balanço da temporada, como a tela de fim de ano mostra. */
export interface SeasonReportView {
  season: number;
  clubName: string;
  leagueName: string;
  position: number;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  points: number;
  goalsFor: number;
  goalsAgainst: number;
  target: number;
  targetLabelKey: string;
  verdict: "acima" | "cumpriu" | "abaixo";
  fired: boolean;
  titles: string[];
  finance: { income: number; expense: number; balance: number; cash: number; debt: number };
  topScorer: { name: string; goals: number } | null;
  bestRated: { name: string; rating: number } | null;
  youth: Array<{ playerId: string; name: string; pos: string; age: number; strength: number }>;
  expiring: Array<{
    playerId: string;
    name: string;
    pos: string;
    age: number;
    strength: number;
    salary: number;
    /** Renovação já está na fila de decisões. */
    renewing: boolean;
  }>;
  offers: JobOfferView[];
  /**
   * Conquistas de carreira destravadas nesta temporada.
   *
   * O motor já as calculava e já as guardava no save, e elas só apareciam na
   * tela de Carreira: quem ganhava a primeira taça não ficava sabendo no
   * balanço do ano em que ganhou.
   */
  achievements: AchievementRow[];
}

/** Um jogador na tela da partida ao vivo. */
export interface LivePlayerRow {
  id: string;
  name: string;
  pos: string;
  /** Posição em que está jogando, que pode não ser a de origem. */
  slot: string;
  strength: number;
  condition: number;
}

/** Estado da partida que o treinador está assistindo. */
/**
 * Um placar da rodada, fora o do treinador.
 *
 * Só o que cabe numa linha de placar: quem joga, quanto está e em que minuto.
 * Os lances de cada uma ficam no motor e não atravessam, porque oitenta
 * narrações por passo seriam oitenta listas que ninguém lê.
 */
export interface PlacarDaRodada {
  fixtureId: string;
  competicao: string;
  homeShort: string;
  homeName: string;
  homeColors: [string, string];
  homeGoals: number;
  awayShort: string;
  awayName: string;
  awayColors: [string, string];
  awayGoals: number;
  minute: number;
  finished: boolean;
  /** O confronto do clube do jogador, que a lista marca. */
  minha: boolean;
}

export interface LiveView {
  fixtureId: string;
  competition: string;
  stage: string;
  homeId: string;
  homeName: string;
  homeShort: string;
  homeColors: [string, string];
  awayId: string;
  awayName: string;
  awayShort: string;
  awayColors: [string, string];
  homeGoals: number;
  awayGoals: number;
  minute: number;
  segment: number;
  totalSegments: number;
  /** A partida parou numa pausa de substituição. */
  atPause: boolean;
  finished: boolean;
  myClubId: string;
  iAmHome: boolean;
  events: Array<{
    minute: number;
    kind: string;
    clubId: string | null;
    key: string;
    params: Record<string, string | number>;
  }>;
  onPitch: LivePlayerRow[];
  bench: LivePlayerRow[];
  /** As outras partidas da rodada, em andamento junto com esta. */
  placares: PlacarDaRodada[];
  subsLeft: number;
}

/** Uma temporada na carreira do treinador. */
export interface CareerSeason {
  season: number;
  clubName: string;
  competitionName: string;
  position: number;
  titleNames: string[];
}

/** Conquista de carreira, ganha ou ainda por ganhar. */
export interface AchievementRow {
  /** O que fica gravado na carreira. Nunca muda, e não aparece na tela. */
  id: string;
  /** Onde o dicionário guarda o nome e a descrição dela. */
  key: string;
  /** Temporada em que veio, ou nulo quando ainda não veio. */
  season: number | null;
}

export interface CareerView {
  managerName: string;
  reputation: number;
  titles: number;
  seasons: CareerSeason[];
  achievements: AchievementRow[];
}

export interface TableRow {
  position: number;
  clubId: string;
  name: string;
  short: string;
  colors: [string, string];
  played: number;
  wins: number;
  draws: number;
  losses: number;
  goalsFor: number;
  goalsAgainst: number;
  points: number;
  isMine: boolean;
}

/**
 * Uma notícia como ela sai do mundo.
 *
 * Com `key`, a frase mora no dicionário e a tela a resolve no idioma de quem
 * lê. Sem `key`, é notícia de save anterior ao esquema 8, que só tem a frase
 * pronta em português e é mostrada como veio: apagar o histórico de alguém para
 * arrumar tradução seria pior que o problema.
 */
export interface NewsRow {
  id: string;
  kind: string;
  key: string | null;
  params: Readonly<Record<string, string | number>>;
  /** Preenchidos só na notícia sem chave. */
  title: string;
  body: string;
  round: number;
}

export interface RivalRow {
  clubId: string;
  name: string;
  short: string;
  colors: [string, string];
  intensity: number;
  /** Chave do rótulo da rivalidade, derivada da intensidade pelo motor. */
  labelKey: string;
}

export interface OfferRow {
  id: string;
  playerId: string;
  playerName: string;
  playerPos: string;
  playerStrength: number;
  otherClubId: string;
  otherClubName: string;
  otherClubShort: string;
  otherClubColors: [string, string];
  amount: number;
  salary: number;
  years: number;
  status: string;
  /** Rodada em que caduca se o treinador não responder. */
  expiresRound: number;
  answered: boolean;
}

export interface MatchSummary {
  homeName: string;
  homeShort: string;
  homeColors: [string, string];
  awayName: string;
  awayShort: string;
  awayColors: [string, string];
  homeGoals: number;
  awayGoals: number;
  competitionName: string;
  stage: string;
  derby: boolean;
  myResult: "V" | "E" | "D";
  events: Array<{
    minute: number;
    kind: string;
    key: string;
    params: Record<string, string | number>;
    mine: boolean;
    clubShort: string;
  }>;
  /** O "Por que aconteceu", como chave e valores. Quem mostra é quem traduz. */
  factors: Array<{ key: string; params: Readonly<Record<string, string | number>> }>;
  possession: number;
  chances: number;
  opponentChances: number;
}

/** Tática enviada pelo treinador. Espelha o tipo do motor. */
export interface TacticInput {
  formation: string;
  style: "defensivo" | "equilibrado" | "ofensivo";
  marking: "leve" | "normal" | "forte";
  tempo: "baixo" | "normal" | "alto";
  takers: { penalty: string | null; freeKick: string | null; corner: string | null };
  captainId: string | null;
}

export interface LineupInput {
  starters: string[];
  bench: string[];
  autoSubs: Array<{ outId: string; inId: string; minute: number }>;
}

/**
 * Uma decisão já na fila, descrita para a tela mostrar o que está pendente.
 *
 * A descrição era uma frase pronta em português. Virou chave e valores pelo
 * motivo de sempre, e de quebra o `playerId` tirou duas buscas por pedaço de
 * texto: a tela de mercado e o balanço de fim de temporada procuravam o nome do
 * jogador dentro da frase para saber se a ordem dele já estava na fila.
 */
export interface QueuedDecision extends FraseDaSessao {
  index: number;
  kind: string;
  /** O jogador ou o garoto a que a ordem se refere. Nulo no que é do clube. */
  playerId: string | null;
  /** A proposta respondida, e como. Nulos em toda ordem que não é resposta. */
  offerId: string | null;
  offerAction: "aceitar" | "recusar" | null;
}

/**
 * Uma linha do calendário do clube.
 *
 * O calendário mistura os quatro torneios de propósito. A pergunta que ele
 * responde não é "como vai a liga", que a tabela já responde, e sim "o que vem
 * pela frente": e o que vem pela frente pode ser um jogo do estadual na quarta
 * e um da copa no domingo.
 */
export interface CalendarRow {
  id: string;
  round: number;
  competition: string;
  competitionType: "league" | "estadual" | "cup" | "continental";
  stage: string;
  opponent: string;
  opponentShort: string;
  opponentColors: [string, string];
  home: boolean;
  neutral: boolean;
  derby: boolean;
  /** Chave do rótulo da rivalidade, quando houver. Nula nos jogos comuns. */
  derbyLabelKey: string | null;
  played: boolean;
  goalsFor: number | null;
  goalsAgainst: number | null;
}

/**
 * O adversário do próximo jogo, do jeito que a tela de tática mostra.
 *
 * A escalação vem na ordem das vagas da formação, igual à do seu time, para o
 * mesmo desenho de campo servir aos dois.
 */
export interface OpponentView {
  name: string;
  short: string;
  colors: [string, string];
  position: number;
  points: number;
  hasTable: boolean;
  form: string[];
  formation: string;
  slots: string[];
  style: TacticInput["style"];
  marking: TacticInput["marking"];
  tempo: TacticInput["tempo"];
  /** Os onze, na ordem de `slots`, com a nota de cada um naquela vaga. */
  starters: Array<{ name: string; pos: string; rating: number }>;
  /** Até três: artilheiro, garçom e capitão, sem repetir jogador. */
  highlights: Array<{ name: string; pos: string; rating: number; kind: "gols" | "assistencias" | "capitao"; value: number }>;
  /** Quem não joga ou está por um cartão. */
  absences: Array<{ name: string; pos: string; kind: "lesao" | "suspenso" | "pendurado"; count: number }>;
}

export interface ClubView {
  season: number;
  round: number;
  totalRounds: number;
  phase: string;
  managerName: string;
  managerReputation: number;
  saveId: string | null;
  club: {
    id: string;
    name: string;
    short: string;
    colors: [string, string];
    /**
     * O escudo e o uniforme desenhados, quando o clube foi criado pela pessoa.
     *
     * `null` em todo clube que o mundo gerou, e aí quem desenha cai no escudo
     * de duas cores de sempre. Sem estes dois campos o criador seria escrita
     * apenas: a pessoa desenha, o save guarda, e nenhuma tela mostra.
     */
    crest: ClubCrest | null;
    kit: ClubKit | null;
    countryName: string;
    founded: number;
    /** Quem fundou, quando foi gente. `null` nos 320 que o mundo gerou. */
    founder: string | null;
    cash: number;
    debt: number;
    reputation: number;
    /** O porte do clube, como chave de rótulo do motor. */
    reputationKey: string;
    ticketPrice: number;
    stadiumCapacity: number;
    form: string[];
    leagueName: string;
    position: number;
    hasTable: boolean;
    /** O objetivo da diretoria, como chave de rótulo do motor. */
    boardLabelKey: string;
    boardPatience: number;
    moraleAverage: number;
  };
  tactic: TacticInput;
  lineup: LineupInput;
  /** Formações disponíveis e as posições de cada slot. */
  formations: Array<{ name: string; slots: string[] }>;
  /**
   * Para cada posição, o elenco ordenado por quanto rende ali, já contando
   * improviso, moral e cansaço. É o que a tela de tática usa para sugerir quem
   * entra em cada vaga.
   */
  fitByPosition: Record<string, Array<{ id: string; rating: number; available: boolean }>>;
  finance: {
    cash: number;
    incomePerRound: number | null;
    expensePerRound: number | null;
    wagesPerRound: number;
    gate: number;
    sponsor: number;
    tv: number;
    prizes: number;
  };
  record: {
    played: number;
    wins: number;
    draws: number;
    losses: number;
    goalsFor: number;
    goalsAgainst: number;
    points: number;
  };
  squad: PlayerRow[];
  /** Quem está em formação na base, do mais velho para o mais novo. */
  youth: YouthRow[];
  table: TableRow[];
  rivals: RivalRow[];
  offersIn: OfferRow[];
  offersOut: OfferRow[];
  marketOpen: boolean;
  /** Rodada em que a janela reabre, quando está fechada. */
  marketOpensRound: number | null;
  pending: QueuedDecision[];
  /** O adversário do próximo jogo, para montar a escalação olhando para ele. */
  opponent: OpponentView | null;
  nextMatch: {
    opponent: string;
    opponentShort: string;
    opponentColors: [string, string];
    home: boolean;
    competition: string;
    stage: string;
    round: number;
    derby: boolean;
  } | null;
  /** Temporada inteira do clube, dos quatro torneios, em ordem de rodada. */
  calendar: CalendarRow[];
  news: NewsRow[];
  seasonOver: boolean;
  /** Preenchido só quando a temporada acabou e há balanço para mostrar. */
  seasonReport: SeasonReportView | null;
  management: ManagementView;
  /** Nulo quando o país do clube não tem copa configurada. */
  cup: CupView | null;
  continental: ContinentalView | null;
  /** Histórico e conquistas do treinador. */
  career: CareerView;
  /**
   * O bastante para reproduzir um defeito, e nada além.
   *
   * A simulação é determinística, então semente, temporada e rodada bastam.
   * Vai junto do relatório de erro. O save nunca vai.
   */
  debug: { seed: string; engineVersion: string; schemaVersion: number };
  /**
   * Treinadores humanos desta partida.
   *
   * Com um só, a barra de troca não aparece. Com mais de um, é hotseat e cada
   * um enfileira as ordens do seu clube antes de a rodada rodar.
   */
  seats: Array<{ clubId: string; clubName: string; clubShort: string; colors: [string, string]; managerName: string; active: boolean; pending: number }>;
  /** Campeonato estadual do clube. Nulo em país sem estados configurados. */
  state: { id: string; name: string; rows: TableRow[]; championName: string | null } | null;
  scorers: ScorerRow[];
}

/** Uma perna de confronto de copa. */
export interface CupLeg {
  round: number;
  homeGoals: number | null;
  awayGoals: number | null;
  played: boolean;
  shootoutWinner: string | null;
}

export interface CupTieView {
  homeId: string;
  homeName: string;
  homeShort: string;
  homeColors: [string, string];
  awayId: string;
  awayName: string;
  awayShort: string;
  awayColors: [string, string];
  legs: CupLeg[];
  aggHome: number;
  aggAway: number;
  winnerId: string | null;
  mine: boolean;
}

export interface CupStageView {
  /** A chave da fase, como o motor a guarda. Quem escreve o nome é o i18n. */
  key: string;
  legs: number;
  rounds: number[];
  status: "passada" | "atual" | "futura";
  ties: CupTieView[];
}

/**
 * Onde o clube está numa copa, como dado e não como frase.
 *
 * A visão é montada dentro do Web Worker, que tem a própria cópia do i18n e
 * nunca soube o idioma escolhido: frase escrita lá chega em português na tela
 * de quem joga em inglês. O que atravessa é a situação, e quem escreve é a tela.
 */
export interface CupRunView {
  situacao:
    | "campeao"
    | "tituloDeOutro"
    | "vivo"
    | "vivoSemFase"
    | "eliminado"
    | "foraDaCopa"
    | "naoClassificou"
    | "grupos"
    | "gruposComMeu"
    | "eliminadoNosGrupos";
  /** Nome do clube campeão, quando a frase cita um. */
  clube: string;
  /** A chave da fase citada, quando a frase cita uma. */
  fase: string;
  /** O grupo do clube, quando a frase cita um. */
  grupo: string;
}

/** Uma chave da fase de grupos continental. */
export interface GroupView {
  name: string;
  rows: TableRow[];
  /** O clube do jogador está neste grupo. */
  mine: boolean;
}

export interface ContinentalView extends CupView {
  phase: "grupos" | "mata-mata";
  groups: GroupView[];
  /** O clube do jogador disputa esta edição. */
  qualified: boolean;
}

export interface CupView {
  id: string;
  name: string;
  championName: string | null;
  stages: CupStageView[];
  /** Onde o clube do jogador está na copa. */
  myStatus: CupRunView;
}

export interface ScorerRow {
  playerId: string;
  name: string;
  pos: string;
  clubName: string;
  clubShort: string;
  clubColors: [string, string];
  goals: number;
  assists: number;
  apps: number;
  mine: boolean;
}

/** Estrutura do clube: nível atual, próximo passo e o que ele muda. */
export interface FacilityView {
  key: string;
  /** O nome da estrutura, como chave de rótulo do motor. */
  nameKey: string;
  level: number;
  /** O que ela muda, como chave do dicionário da tela. */
  effectKey: string;
  nextLevel: number | null;
  nextCost: number;
  nextRounds: number;
  upkeep: number;
}

/**
 * Uma recusa do motor, como chave e valores.
 *
 * O motor deixou de devolver a frase pronta: quem escreve é o dicionário de
 * quem está lendo. A tela resolve com `motivoDoMotor`.
 */
export type Recusa = FraseDaSessao;

/**
 * O que deu errado numa ordem.
 *
 * Uma chave e os valores dela, venha ela do motor ou da sessão solo. Já foi
 * `string | Recusa`, e o lado `string` era exatamente o buraco: era por ele que
 * a frase em português do worker chegava à tela de quem joga em inglês.
 */
export type ErroDaSessao = FraseDaSessao;

/**
 * Erro da sessão que a tela sabe escrever.
 *
 * Existe porque nem toda recusa cabe num valor de retorno: `requireWorld`, a
 * leitura de um save que sumiu e a abertura do IndexedDB precisam interromper o
 * que estavam fazendo. Um `Error` comum carregaria uma frase, e frase escrita
 * aqui dentro sai em português para todo mundo; este carrega a chave.
 */
export class ErroComChave extends Error {
  readonly key: string;
  readonly params: Record<string, string | number>;

  constructor(key: string, params: Record<string, string | number> = {}) {
    super(key);
    this.name = "ErroComChave";
    this.key = key;
    this.params = params;
  }
}

/**
 * A recusa correspondente a qualquer coisa que tenha sido lançada.
 *
 * As três origens que interessam já vêm com chave: a da sessão, a do motor ao
 * abrir um save e a que atravessou o worker. O que sobra é defeito de verdade,
 * e aí a mensagem crua vai junto: ela não está traduzida, mas é a única coisa
 * que explica o que houve.
 */
export function recusaDoErro(erro: unknown): ErroDaSessao {
  if (erro instanceof ErroComChave) return { key: erro.key, params: { ...erro.params } };
  if (erro instanceof SaveError) return { key: erro.key, params: { ...erro.params } };
  const detalhe = erro instanceof Error ? erro.message : String(erro);
  return { key: "sessao.falhaInesperada", params: { detalhe } };
}

/** Uma parcela de transferência a vencer, do lado de quem paga ou recebe. */
export interface PaymentRow {
  playerName: string;
  amount: number;
  /** Em quantas rodadas vence. Negativo nunca aparece. */
  dueIn: number;
  incoming: boolean;
}

export interface ManagementView {
  facilities: FacilityView[];
  build: { nameKey: string; targetLevel: number; roundsLeft: number } | null;
  loanLimit: number;
  loanAvailable: number;
  debt: number;
  debtRatePerRound: number;
  ticketPrice: number;
  /** Público e receita previstos em vários preços, para escolher com base. */
  /** Parcelas de transferência a pagar e a receber. */
  payments: PaymentRow[];
  owed: number;
  receivable: number;
  ticketCurve: Array<{ price: number; attendance: number; revenue: number; current: boolean }>;
}

export interface MarketFilters {
  text: string;
  position: string | null;
  maxAge: number | null;
  minStrength: number | null;
  maxValue: number | null;
  freeAgentsOnly: boolean;
  affordableOnly: boolean;
}

/**
 * A coluna que ordena a busca do mercado, e para que lado.
 *
 * A ordem vai ao motor junto com a página: ordenar só a página aberta faria
 * a "mais barata" ser a mais barata de cinquenta, e não dos oito mil.
 */
export interface OrdemDoMercado {
  chave: string;
  direcao: "asc" | "desc";
}

/** Quantos jogadores cabem numa página do mercado. */
export const JOGADORES_POR_PAGINA = 50;

export interface MarketPlayerRow {
  id: string;
  name: string;
  pos: string;
  age: number;
  strength: number;
  highlights: string[];
  value: number;
  salary: number;
  clubId: string | null;
  clubName: string;
  clubShort: string;
  clubColors: [string, string];
  /** Nulo quando o jogador não aceitaria vir de jeito nenhum. */
  askingSalary: number | null;
  askingPrice: number;
  affordable: boolean;
  contractEndsSeason: number;
  transferListed: boolean;
}

export type Command =
  | { type: "create"; seed: string }
  | { type: "pick"; clubId: string; managerName: string }
  /** `salvar: false` pula o salvamento automático da rodada. */
  | { type: "advance"; salvar?: boolean }
  | { type: "view" }
  | { type: "setLineup"; lineup: LineupInput; tactic: TacticInput }
  | { type: "autoLineup"; tactic?: TacticInput }
  /** Compra na hora, pelo preço de compra da ficha. */
  | { type: "buyNow"; playerId: string; years: number; salvar?: boolean }
  | {
      type: "offer";
      playerId: string;
      amount: number;
      salary: number;
      years: number;
      installments: number;
    }
  | { type: "scout"; playerId: string }
  | { type: "takeJob"; clubId: string }
  | { type: "liveStart" }
  | { type: "liveStep"; untilSegment: number }
  | { type: "liveSub"; outId: string; inId: string }
  | { type: "liveFinish"; salvar?: boolean }
  /**
   * `saveId` vem de fora quando a carreira é da conta.
   *
   * No modo 1 Jogador a sessão inventa o identificador, porque ele só serve
   * para achar o arquivo aqui dentro. Na carreira do servidor ele precisa ser
   * o mesmo sempre: é a chave do cache local daquela conta, e um identificador
   * novo a cada início deixaria uma carreira órfã no aparelho por temporada.
   */
  | {
      type: "pickMany";
      seats: Array<{ clubId: string; managerName: string }>;
      saveId?: string;
    }
  | {
      /**
       * Cria o clube da pessoa e assume o comando dele.
       *
       * Vem separado de `pickMany` porque não é escolher entre o que existe: o
       * clube nasce aqui, entra na última divisão e o calendário é refeito
       * antes de alguém pegar o comando.
       */
      type: "criarClube";
      entrada: EntradaDeClubeCriado;
      managerName: string;
      saveId?: string;
    }
  | { type: "switchSeat"; index: number }
  | { type: "createFromPack"; seed: string; pack: unknown }
  | { type: "makePack"; seed: string }
  | { type: "exportPack" }
  | { type: "loanIn"; playerId: string; salaryShare: number }
  | { type: "respondOffer"; offerId: string; action: "aceitar" | "recusar" }
  | { type: "playerAction"; playerId: string; action: string; salary?: number; years?: number }
  | { type: "youthAction"; prospectId: string; action: YouthAction }
  | {
      type: "clubAction";
      ticketPrice?: number;
      loan?: number;
      repay?: number;
      build?: { facility: string; targetLevel: number };
    }
  | { type: "cancelDecision"; index: number }
  | { type: "search"; filters: MarketFilters; pagina?: number; ordem?: OrdemDoMercado | null }
  | { type: "detail"; playerId: string }
  | { type: "save" }
  | { type: "load"; saveId: string }
  | { type: "listSaves" }
  | { type: "deleteSave"; saveId: string };

/**
 * Um problema de datapack como o worker o devolve.
 *
 * A mensagem não vem escrita: vem a chave do motor e os valores dela. O
 * caminho vem como está, porque ele não é texto, é endereço, e sai igual em
 * qualquer idioma.
 */
export interface ProblemaDoPacote extends FraseDaSessao {
  path: string;
}

export type Reply =
  | { type: "live"; live: LiveView | null; error: ErroDaSessao | null }
  | { type: "pack"; name: string; json: string }
  /**
   * Banco recém-gerado, em objeto e não em texto.
   *
   * O editor recebe o datapack pronto para editar. Passar o JSON em texto
   * obrigaria a interface a analisar alguns megabytes na thread da tela, e é
   * justamente ela que não pode travar.
   */
  | { type: "packData"; name: string; pack: Datapack }
  | {
      type: "created";
      options: ClubOption[];
      generationMs: number;
      /** O que impede o arquivo de virar carreira. Vazio quando ele serve. */
      problems: ProblemaDoPacote[];
    }
  | { type: "view"; view: ClubView; error: ErroDaSessao | null }
  | {
      type: "advanced";
      view: ClubView;
      match: MatchSummary | null;
      /** O recado da barra de ações quando o time não jogou. Chave, não frase. */
      note: FraseDaSessao | null;
      roundMs: number;
    }
  | { type: "search"; players: MarketPlayerRow[]; total: number; pagina: number; paginas: number }
  | { type: "detail"; player: PlayerDetail }
  | { type: "saves"; saves: SaveMeta[] }
  | { type: "saved"; meta: SaveMeta };
